import {inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {LocalstorageService} from 'src/app/shared/services/localstorage.service';
import {environment} from 'src/environments/environment';
import {IUser} from 'src/app/models/user.model';
import {Observable, ReplaySubject} from 'rxjs';
import {tap} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private readonly lsService: LocalstorageService = inject(LocalstorageService);
    private readonly http: HttpClient = inject(HttpClient);
    private readonly router: Router = inject(Router);

    currentUserSubject = new ReplaySubject<IUser>(1);
    currentUser = signal<IUser>({} as IUser);
    private readonly apiURL: string = environment.endpointAPI;

    constructor() {
        this.startCurrentUser();
    }

    startCurrentUser(): void {
        this.setCurrentUser();
    }

    getCurrentUserObservable(): Observable<IUser> {
        return this.currentUserSubject.asObservable();
    }

    setCurrentUser(): void {
        this.currentUserSubject.next(this.currentUser());
    }

    authenticate(email: string, password: string): Observable<IUser> {
        return this.http.post<IUser>(`${this.apiURL}/auth/signin/v2`, {email, password}, {
            withCredentials: true
        });
    }

    authenticateMfa(code: string, token: string): Observable<IUser> {
        return this.http.post<IUser>(`${this.apiURL}/auth/signin/mfa`, { code, token }, {
            withCredentials: true
        });
    }

    authenticateFederation(token: string): Observable<IUser> {
        return this.http.get<IUser>(`${this.apiURL}/auth/signin/federation/user`, {
            params: {"token": token},
            withCredentials: true
        });
    }

    sendBeginRecoveryPassword(email: string) {
        return this.http.post(`${this.apiURL}/auth/password/beginReset`, {email});
    }

    checkRecoveryPassword(hash: string) {
        return this.http.post(`${this.apiURL}/auth/password/checkReset`, {hash});
    }

    sendEndRecoveryPassword(password: string, hash: string) {
        return this.http.post(`${this.apiURL}/auth/password/endReset`, {hash, password});
    }

    refreshToken(): Observable<any> {
        return this.http.post<any>(`${this.apiURL}/auth/signin/refresh`, {}, {
            withCredentials: true
        }).pipe(
            tap((response: any) => {
                const userData = response.data.user;
                this.setUser(userData);
            })
        );
    }

    setUser(userData: IUser): void {
        this.currentUser.set(userData);
        this.setCurrentUser();
    }

    logout(): void {
        this.http.post<IUser>(`${this.apiURL}/auth/signin/logout`, {}, {
            withCredentials: true
        }).subscribe({
            next: () => {
                this.currentUser.set({} as IUser);
                this.currentUserSubject.next({} as IUser);
                this.lsService.removeItem('session_id');
                this.router.navigateByUrl('/');
            },
            error: () => {
                this.currentUser.set({} as IUser);
                this.currentUserSubject.next({} as IUser);
                this.lsService.removeItem('session_id');
                this.router.navigateByUrl('/');
            }
        });
    }
}
